import { GOODS_ENDPOINT, webCore } from '@lemon/web-core';

import type { ProductView } from '@lemoncloud/epyt-goods-api';

const GOODS_API = GOODS_ENDPOINT;
export const GOODS_API_ERROR_CODES = {
    NOT_FOUND: 'GOODS_NOT_FOUND',
} as const;

type GoodsApiErrorCode = (typeof GOODS_API_ERROR_CODES)[keyof typeof GOODS_API_ERROR_CODES];

type GoodsApiError = Error & {
    code?: GoodsApiErrorCode;
    error?: string;
    message?: string;
    status?: number;
    statusCode?: number;
    response?: {
        status?: number;
        data?: {
            error?: string;
            message?: string;
        };
    };
};

type GoodsApiErrorPayload = {
    error?: string;
    message?: string;
};

const asErrorPayload = (payload: unknown): GoodsApiErrorPayload | undefined => {
    if (!payload || typeof payload !== 'object') {
        return undefined;
    }

    const candidate = payload as GoodsApiErrorPayload;

    if (typeof candidate.error === 'string' || typeof candidate.message === 'string') {
        return candidate;
    }

    return undefined;
};

const getPayloadErrorText = (payload: unknown): string => {
    const candidate = asErrorPayload(payload);

    return candidate?.error?.trim() || candidate?.message?.trim() || '';
};

const getErrorStatus = (error: unknown): number | undefined => {
    const candidate = error as GoodsApiError | undefined;

    return candidate?.status || candidate?.response?.status || candidate?.statusCode;
};

const getErrorText = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }

    const candidate = error as GoodsApiError | undefined;

    return (
        candidate?.message ||
        candidate?.error ||
        candidate?.response?.data?.message ||
        candidate?.response?.data?.error ||
        ''
    );
};

const isNotFoundResponse = (error: unknown): boolean => {
    if (getErrorStatus(error) === 404) {
        return true;
    }

    const errorText = getErrorText(error).toUpperCase();

    return errorText.includes('404') && errorText.includes('NOT FOUND');
};

const createGoodsNotFoundError = (targetId: string, cause: unknown): GoodsApiError => {
    const error = new Error(`Goods not found: ${targetId}`) as GoodsApiError & {
        cause?: unknown;
    };

    error.name = 'GoodsNotFoundError';
    error.code = GOODS_API_ERROR_CODES.NOT_FOUND;
    error.status = 404;
    error.cause = cause;

    return error;
};

export const isGoodsNotFoundError = (error: unknown): boolean => {
    const candidate = error as GoodsApiError | undefined;

    if (candidate?.code === GOODS_API_ERROR_CODES.NOT_FOUND) {
        return true;
    }

    return isNotFoundResponse(error);
};

export interface FetchGoodsParams {
    id: string | number;
}

export const fetchGoodsById = async (params: FetchGoodsParams): Promise<ProductView> => {
    const targetId = String(params.id).trim();

    if (!targetId) {
        throw new Error('fetchGoodsById: @id is required');
    }

    const baseURL = `${GOODS_API}/public/${encodeURIComponent(targetId)}/goods`;

    try {
        const { data } = await webCore
            .buildRequest({
                method: 'GET',
                baseURL,
            })
            .execute<ProductView>();

        const payloadErrorText = getPayloadErrorText(data);

        if (payloadErrorText) {
            if (isNotFoundResponse(data)) {
                throw createGoodsNotFoundError(targetId, data);
            }

            const payloadError = new Error(payloadErrorText) as GoodsApiError;
            payloadError.name = 'GoodsApiResponseError';
            payloadError.error = payloadErrorText;
            payloadError.response = {
                status: 200,
                data: {
                    error: payloadErrorText,
                },
            };
            throw payloadError;
        }

        return data;
    } catch (error) {
        if (isNotFoundResponse(error)) {
            throw createGoodsNotFoundError(targetId, error);
        }

        throw error;
    }
};
